/**
 * AiPromptCounter — pill-style usage indicator for the 5-prompt cap.
 *
 * Renders "X of 5 used" with colour progression:
 *   - 0–3 used  → primary (teal)
 *   - 4 used    → warning (amber)
 *   - 5 used    → error (red) + "Limit reached" label
 *
 * IMPORTANT: this is a UX affordance only. The real enforcement is server-side
 * (VER-55 / VER-58 backend) — the server must reject prompt 6 with HTTP 429.
 */

import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { AI_PROMPT_LIMIT } from "./assessmentAi.mock";

type AiPromptCounterProps = {
    promptsUsed: number;
};

function resolveColor(used: number): "primary" | "warning" | "error" {
    if (used >= AI_PROMPT_LIMIT) {
        return "error";
    }
    if (used >= AI_PROMPT_LIMIT - 1) {
        return "warning";
    }
    return "primary";
}

export const AiPromptCounter = ({ promptsUsed }: AiPromptCounterProps): JSX.Element => {
    const color = resolveColor(promptsUsed);
    const isAtLimit = promptsUsed >= AI_PROMPT_LIMIT;
    const label = isAtLimit ? "Limit reached" : `${promptsUsed} of ${AI_PROMPT_LIMIT} used`;

    return (
        <Tooltip
            title={
                isAtLimit
                    ? "You have used all 5 AI prompts for this session. The limit is enforced by the server to protect participant data."
                    : `You have ${AI_PROMPT_LIMIT - promptsUsed} prompt${AI_PROMPT_LIMIT - promptsUsed === 1 ? "" : "s"} remaining in this session.`
            }
        >
            <Box
                display="inline-flex"
                alignItems="center"
                gap={0.5}
                px={1.5}
                py={0.5}
                sx={{
                    borderRadius: 999,
                    border: "1px solid",
                    borderColor: `${color}.main`,
                    bgcolor: `${color}.light`,
                    cursor: "default",
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 600, color: `${color}.dark` }}>
                    {label}
                </Typography>
                <InfoOutlined sx={{ fontSize: "0.875rem", color: `${color}.main` }} />
            </Box>
        </Tooltip>
    );
};
